import { useCallback } from "react";
import { useAppAuth } from "@/auth/use-app-auth";
import { useQuery } from "@tanstack/react-query";
import { Axios } from "@/lib/axios";

// ============================================================================
// Feature-level usage data (detailed view for all 17 features)
// ============================================================================

export interface FeatureUsage {
  featureKey: string;
  displayName: string;
  used: number;
  includedLimit: number | null;
  packsRemaining: number;
  remaining: number | null;
}

// ============================================================================
// Backward-compatible interfaces (for existing components)
// ============================================================================

export interface UsageData {
  jobPostsUsed: number;
  resumesProcessed: number;
  interviewsUsed: number;
  searchesUsed: number;
}

export interface UsageLimits {
  jobPostLimit: number | null;
  resumeLimit: number;
  interviewLimit: number;
  searchLimit: number | null;
}

// ============================================================================
// Full usage summary from backend
// ============================================================================

export interface FullUsageSummary {
  billingEntity: "org";
  entityId: string;
  orgDbId?: string | null;
  tier: string;
  periodStart: string;
  periodEnd: string;
  features: FeatureUsage[];
}

// Query key for consistent caching
const QUERY_KEYS = {
  usage: ["pricing", "my-usage"] as const,
};

// ============================================================================
// Key features to display on dashboard (ordered by importance)
// ============================================================================

export const DASHBOARD_FEATURE_KEYS = [
  "active_jobs",
  "byor_uploads",
  "ai_interviews",
  "candidate_searches",
  "ai_candidate_ranking",
  "companies",
  "ai_job_generation",
] as const;

export type DashboardFeatureKey = typeof DASHBOARD_FEATURE_KEYS[number];

// ============================================================================
// Hook: useUsage - Consolidated usage data hook
// ============================================================================

export const useUsage = () => {
  const { isAuthenticated } = useAppAuth();

  // Fetch usage data from the detailed pricing endpoint
  const {
    data: fullUsage = null,
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    queryKey: QUERY_KEYS.usage,
    queryFn: async (): Promise<FullUsageSummary | null> => {
      try {
        const response = await Axios.get("/api/pricing/my-usage");
        return response.data as FullUsageSummary;
      } catch (error: any) {
        if (error?.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: isAuthenticated,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes garbage collection
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
    refetchIntervalInBackground: false,
  });

  // ============================================================================
  // Helper to find feature by key
  // ============================================================================

  const getFeature = useCallback(
    (featureKey: string): FeatureUsage | undefined => {
      return fullUsage?.features.find((f) => f.featureKey === featureKey);
    },
    [fullUsage]
  );

  // ============================================================================
  // Get dashboard-relevant features only
  // ============================================================================

  const getDashboardFeatures = useCallback((): FeatureUsage[] => {
    if (!fullUsage) return [];
    return DASHBOARD_FEATURE_KEYS.map((key) => getFeature(key)).filter(
      (f): f is FeatureUsage => f !== undefined
    );
  }, [fullUsage, getFeature]);

  // ============================================================================
  // Backward-compatible usage data (maps to old interface)
  // ============================================================================

  const usage: UsageData | null = fullUsage
    ? {
      jobPostsUsed: getFeature("active_jobs")?.used ?? 0,
      resumesProcessed: getFeature("byor_uploads")?.used ?? 0,
      interviewsUsed: getFeature("ai_interviews")?.used ?? 0,
      searchesUsed: getFeature("candidate_searches")?.used ?? 0,
    }
    : null;

  const limits: UsageLimits | null = fullUsage
    ? {
      jobPostLimit: getFeature("active_jobs")?.includedLimit ?? null,
      resumeLimit: getFeature("byor_uploads")?.includedLimit ?? 0,
      interviewLimit: getFeature("ai_interviews")?.includedLimit ?? 0,
      searchLimit: getFeature("candidate_searches")?.includedLimit ?? null,
    }
    : null;

  // ============================================================================
  // Utility functions
  // ============================================================================

  const checkLimit = useCallback(
    (type: keyof UsageData): boolean => {
      if (!usage || !limits) return true;
      const used = usage[type];
      const limitKey = `${type.replace("Used", "Limit")}` as keyof UsageLimits;
      const limit = limits[limitKey];
      return limit ? used < limit : true;
    },
    [usage, limits]
  );

  const checkFeatureLimit = useCallback(
    (featureKey: string): boolean => {
      const feature = getFeature(featureKey);
      if (!feature) return true;
      if (feature.remaining === null) return true; // Unlimited
      return feature.remaining > 0;
    },
    [getFeature]
  );

  const getWarningLevel = useCallback(
    (type: keyof UsageData): number => {
      if (!usage || !limits) return 0;
      const used = usage[type];
      const limitKey = `${type.replace("Used", "Limit")}` as keyof UsageLimits;
      const limit = limits[limitKey];
      if (!limit) return 0;
      const percentage = (used / limit) * 100;
      if (percentage >= 100) return 4;
      if (percentage >= 95) return 3;
      if (percentage >= 90) return 2;
      if (percentage >= 80) return 1;
      return 0;
    },
    [usage, limits]
  );

  const getFeatureWarningLevel = useCallback(
    (featureKey: string): number => {
      const feature = getFeature(featureKey);
      if (!feature || feature.includedLimit === null) return 0;
      const total = feature.includedLimit + feature.packsRemaining;
      if (total === 0) return 0;
      const percentage = (feature.used / total) * 100;
      if (percentage >= 100) return 4;
      if (percentage >= 95) return 3;
      if (percentage >= 90) return 2;
      if (percentage >= 80) return 1;
      return 0;
    },
    [getFeature]
  );

  const getUsagePercentage = useCallback(
    (type: keyof UsageData): number => {
      if (!usage || !limits) return 0;
      const used = usage[type];
      const limitKey = `${type.replace("Used", "Limit")}` as keyof UsageLimits;
      const limit = limits[limitKey];
      if (!limit) return 0;
      return Math.min((used / limit) * 100, 100);
    },
    [usage, limits]
  );

  const getFeatureUsagePercentage = useCallback(
    (featureKey: string): number => {
      const feature = getFeature(featureKey);
      if (!feature || feature.includedLimit === null) return 0;
      const total = feature.includedLimit + feature.packsRemaining;
      if (total === 0) return 0;
      return Math.min((feature.used / total) * 100, 100);
    },
    [getFeature]
  );

  const getRemainingUsage = useCallback(
    (type: keyof UsageData): number => {
      if (!usage || !limits) return 0;
      const used = usage[type];
      const limitKey = `${type.replace("Used", "Limit")}` as keyof UsageLimits;
      const limit = limits[limitKey];
      if (!limit) return Infinity;
      return Math.max(limit - used, 0);
    },
    [usage, limits]
  );

  const isNearLimit = useCallback(
    (type: keyof UsageData, threshold = 80): boolean => {
      return getUsagePercentage(type) >= threshold;
    },
    [getUsagePercentage]
  );

  const isAtLimit = useCallback(
    (type: keyof UsageData): boolean => {
      return !checkLimit(type);
    },
    [checkLimit]
  );

  return {
    // Backward-compatible fields
    usage,
    limits,
    planName: fullUsage?.tier || "",
    billingPeriodStart: fullUsage?.periodStart || "",
    billingPeriodEnd: fullUsage?.periodEnd || "",
    loading,
    error: error ? (error as Error).message : null,
    checkLimit,
    getWarningLevel,
    getUsagePercentage,
    getRemainingUsage,
    isNearLimit,
    isAtLimit,
    refetch: () => refetch(),

    // New detailed fields
    fullUsage,
    features: fullUsage?.features || [],
    getFeature,
    getDashboardFeatures,
    checkFeatureLimit,
    getFeatureWarningLevel,
    getFeatureUsagePercentage,
    tier: fullUsage?.tier || "FREE",
    billingEntity: fullUsage?.billingEntity || "org",
  };
};
